(() => {
  const runtimeKey = Symbol.for('clearvero.pen-camera.task3');
  const existingRuntime = window[runtimeKey];
  if (existingRuntime) {
    existingRuntime.boot();
    return;
  }

  const instances = new Map();

  function createInstance(loader) {
    const productId = loader.dataset.cvProductId;
    const variants = JSON.parse(loader.querySelector('[data-variants]').textContent);
    const variantMap = new Map(variants.map((variant) => [String(variant.id), variant]));
    const selector = `[data-cv-product-id="${CSS.escape(productId)}"]`;
    const controller = new AbortController();
    const { signal } = controller;
    let selectedId = String(loader.dataset.selectedVariant);
    let stickyObserver;
    let stickyResizeObserver;
    let updateStickyBar;
    let submitting = false;
    let galleryOpener = null;
    const boundFaqs = new WeakSet();

    const all = (query) => document.querySelectorAll(`${selector} ${query}`);
    const first = (query) => document.querySelector(`${selector} ${query}`);
    const form = () => first('form.cvt2-hero__form');

    function setSubmitState(isSubmitting) {
      submitting = isSubmitting;
      all('[data-primary-add], [data-add-proxy]').forEach((button) => {
        const variant = variantMap.get(selectedId);
        button.disabled = isSubmitting || !variant?.orderable;
        button.setAttribute('aria-busy', isSubmitting ? 'true' : 'false');
      });
      all('[data-add-label]').forEach((label) => {
        label.textContent = isSubmitting ? 'Adding…' : 'Add to Cart';
      });
    }

    function syncQuantity() {
      const input = form()?.querySelector('[name="quantity"]');
      if (!input) return;
      const parsed = Math.floor(Number(input.value));
      const quantity = Number.isFinite(parsed) && parsed >= 1 ? parsed : 1;
      input.value = String(quantity);
      const minimum = Math.max(1, Math.floor(Number(input.min)) || 1);
      all('[data-purchase-quantity]').forEach((node) => {
        node.textContent = `Qty ${quantity}`;
      });
      all('[data-quantity-step="-1"]').forEach((button) => {
        button.disabled = quantity <= minimum;
      });
    }

    function updateMainImage(variant) {
      all('[data-main-image]').forEach((image) => {
        if (!variant.image) return;
        image.src = variant.image;
        image.srcset = '';
        image.alt = variant.imageAlt || `ClearVero Pen Camera ${variant.title}`;
      });
    }

    function updatePaymentTerms() {
      all('shopify-payment-terms').forEach((terms) => {
        terms.setAttribute('variant-id', selectedId);
        try {
          terms.variantId = selectedId;
        } catch (error) {
          // The native element may expose only the observed attribute.
        }
      });
    }

    function setVariant(id, updateUrl = true, announce = true) {
      const variant = variantMap.get(String(id));
      if (!variant) return;
      selectedId = String(variant.id);
      loader.dataset.selectedVariant = selectedId;

      all('[data-option]').forEach((button) => {
        const active = button.dataset.variantId === selectedId;
        button.setAttribute('aria-checked', active ? 'true' : 'false');
        button.tabIndex = active ? 0 : -1;
      });
      all('[data-select-variant]').forEach((button) => {
        const active = button.dataset.variantId === selectedId;
        button.setAttribute('aria-pressed', active ? 'true' : 'false');
        button.dataset.selected = active ? 'true' : 'false';
      });
      all('[data-storage-card]').forEach((card) => {
        const button = card.querySelector('[data-select-variant]');
        card.dataset.selected = button?.dataset.variantId === selectedId ? 'true' : 'false';
      });
      all('[data-price]').forEach((node) => {
        node.textContent = variant.priceFormatted;
      });
      all('[data-package-title]').forEach((node) => {
        node.textContent = variant.title;
      });
      all('[data-availability]').forEach((node) => {
        node.textContent = variant.availabilityLabel;
      });
      all('[data-variant-input]').forEach((input) => {
        input.value = selectedId;
        input.dispatchEvent(new Event('change', { bubbles: true }));
      });
      all('[data-primary-add], [data-add-proxy]').forEach((button) => {
        button.disabled = !variant.orderable;
      });
      all('[data-add-label]').forEach((node) => {
        node.textContent = 'Add to Cart';
      });

      updatePaymentTerms();
      updateMainImage(variant);
      syncQuantity();

      if (updateUrl && window.history?.replaceState) {
        const url = new URL(window.location.href);
        url.searchParams.set('variant', selectedId);
        window.history.replaceState({ ...(window.history.state || {}), variantId: selectedId }, '', url);
      }
      if (announce) {
        const live = loader.querySelector('[data-cv-live]');
        if (live) {
          live.textContent = `${variant.title}. ${variant.priceFormatted}. ${variant.availabilityLabel}.`;
        }
      }
    }

    function bindStickyBar() {
      stickyObserver?.disconnect();
      stickyResizeObserver?.disconnect();
      const bar = first('[data-mobile-bar]');
      if (!bar) return;
      const watched = [first('[data-hero-buy]'), first('[data-final]'), document.querySelector('footer')].filter(Boolean);
      const update = () => {
        const mobile = window.matchMedia('(max-width: 749px)').matches;
        const away = watched.length > 0 && watched.every((element) => {
          const rect = element.getBoundingClientRect();
          return rect.bottom <= 0 || rect.top >= window.innerHeight;
        });
        const visible = mobile && away;
        bar.dataset.visible = visible ? 'true' : 'false';
        bar.setAttribute('aria-hidden', visible ? 'false' : 'true');
        bar.toggleAttribute('inert', !visible);
        bar.querySelectorAll('button').forEach((button) => {
          button.tabIndex = visible ? 0 : -1;
        });
      };
      updateStickyBar = update;
      stickyObserver = new IntersectionObserver(update, { threshold: 0.05 });
      watched.forEach((element) => stickyObserver.observe(element));
      stickyResizeObserver = new ResizeObserver(update);
      stickyResizeObserver.observe(document.documentElement);
      update();
    }

    function bindAuthoritativeForm() {
      const productForm = form();
      if (!productForm) return;
      const formId = productForm.getAttribute('id');
      all('[data-add-proxy]').forEach((button) => {
        button.type = 'submit';
        button.name = 'add';
        button.setAttribute('form', formId);
      });
      productForm.addEventListener('submit', (event) => {
        if (submitting) {
          event.preventDefault();
          return;
        }
        if (!productForm.checkValidity()) {
          event.preventDefault();
          const invalidControl = productForm.querySelector(':invalid');
          invalidControl?.setAttribute('aria-invalid', 'true');
          invalidControl?.focus();
          invalidControl?.reportValidity();
          setSubmitState(false);
          return;
        }
        setSubmitState(true);
      }, { signal });
      productForm.addEventListener('input', (event) => {
        const control = event.target.closest?.('input, select, textarea');
        if (control?.checkValidity()) control.removeAttribute('aria-invalid');
      }, { signal });
      window.addEventListener('pageshow', () => setSubmitState(false), { signal });
    }

    function bindFaqs() {
      all('[data-cvt4-faq]').forEach((details) => {
        if (boundFaqs.has(details)) return;
        boundFaqs.add(details);
        const summary = details.querySelector('summary');
        const sync = () => summary?.setAttribute('aria-expanded', details.open ? 'true' : 'false');
        details.addEventListener('toggle', sync, { signal });
        summary?.addEventListener('keydown', (event) => {
          if (event.repeat || !['Enter', ' '].includes(event.key)) return;
          event.preventDefault();
          details.open = !details.open;
        }, { signal });
        sync();
      });
    }

    document.addEventListener('click', (event) => {
      const quantityStep = event.target.closest?.(`${selector} [data-quantity-step]`);
      if (quantityStep) {
        event.preventDefault();
        const input = form()?.querySelector('[name="quantity"]');
        if (!input) return;
        const minimum = Math.max(1, Math.floor(Number(input.min)) || 1);
        const step = Math.max(1, Math.floor(Number(input.step)) || 1);
        const current = Math.max(minimum, Math.floor(Number(input.value)) || minimum);
        const direction = Number(quantityStep.dataset.quantityStep) < 0 ? -1 : 1;
        input.value = String(Math.max(minimum, current + (direction * step)));
        input.dispatchEvent(new Event('input', { bubbles: true }));
        return;
      }

      const option = event.target.closest?.(`${selector} [data-option], ${selector} [data-select-variant]`);
      if (option && !option.disabled) {
        setVariant(option.dataset.variantId, true, true);
        return;
      }

      const openButton = event.target.closest?.(`${selector} [data-gallery-open]`);
      if (openButton) {
        const dialog = document.getElementById(openButton.getAttribute('aria-controls'));
        if (dialog?.showModal) {
          galleryOpener = openButton;
          dialog.showModal();
          dialog.querySelector('[data-gallery-close]')?.focus();
        }
        return;
      }

      const closeButton = event.target.closest?.(`${selector} [data-gallery-close]`);
      if (closeButton) {
        closeButton.closest('dialog')?.close();
        return;
      }

      const dialog = event.target.matches?.(`${selector} [data-gallery-dialog]`) ? event.target : null;
      if (dialog) dialog.close();
    }, { signal });

    document.addEventListener('keydown', (event) => {
      const openDialog = first('[data-gallery-dialog][open]');
      if (openDialog && event.key === 'Escape') {
        event.preventDefault();
        openDialog.close();
        return;
      }
      if (openDialog && event.key === 'Tab') {
        const focusable = Array.from(openDialog.querySelectorAll(
          'button:not([disabled]), a[href], input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])'
        )).filter((control) => !control.hidden && control.getAttribute('aria-hidden') !== 'true');
        if (!focusable.length) {
          event.preventDefault();
          openDialog.focus();
          return;
        }
        const firstControl = focusable[0];
        const lastControl = focusable[focusable.length - 1];
        const leavingBackward = event.shiftKey && document.activeElement === firstControl;
        const leavingForward = !event.shiftKey && document.activeElement === lastControl;
        if (focusable.length === 1 || leavingBackward || leavingForward) {
          event.preventDefault();
          (event.shiftKey ? lastControl : firstControl).focus();
        }
        return;
      }

      const option = event.target.closest?.(`${selector} [data-option]`);
      if (!option || !['ArrowLeft', 'ArrowRight', 'ArrowUp', 'ArrowDown'].includes(event.key)) return;
      const controls = Array.from(option.closest('[role="radiogroup"]')?.querySelectorAll('[data-option]:not(:disabled)') || []);
      if (!controls.length) return;
      const direction = ['ArrowRight', 'ArrowDown'].includes(event.key) ? 1 : -1;
      const next = controls[(controls.indexOf(option) + direction + controls.length) % controls.length];
      event.preventDefault();
      next.focus();
      setVariant(next.dataset.variantId, true, true);
    }, { signal });

    document.addEventListener('input', (event) => {
      if (event.target.matches?.(`${selector} [name="quantity"]`)) syncQuantity();
    }, { signal });

    all('[data-gallery-dialog]').forEach((dialog) => {
      dialog.addEventListener('close', () => {
        galleryOpener?.focus();
        galleryOpener = null;
      }, { signal });
    });

    window.addEventListener('popstate', () => {
      const requested = new URL(window.location.href).searchParams.get('variant');
      setVariant(variantMap.has(String(requested)) ? requested : selectedId, false, false);
    }, { signal });

    bindAuthoritativeForm();
    bindFaqs();
    const requested = new URL(window.location.href).searchParams.get('variant');
    setVariant(variantMap.has(String(requested)) ? requested : selectedId, false, false);
    bindStickyBar();
    window.addEventListener('scroll', () => updateStickyBar?.(), { passive: true, signal });

    return {
      loader,
      refresh() {
        setVariant(selectedId, false, false);
        bindFaqs();
        bindStickyBar();
      },
      destroy() {
        controller.abort();
        stickyObserver?.disconnect();
        stickyResizeObserver?.disconnect();
        instances.delete(productId);
      }
    };
  }

  function boot() {
    instances.forEach((instance) => {
      if (!instance.loader.isConnected) instance.destroy();
    });
    document.querySelectorAll('[data-cvh-loader]').forEach((loader) => {
      const productId = loader.dataset.cvProductId;
      const current = instances.get(productId);
      if (current?.loader === loader) current.refresh();
      else {
        current?.destroy();
        instances.set(productId, createInstance(loader));
      }
    });
  }

  document.addEventListener('shopify:section:load', boot);
  document.addEventListener('shopify:block:select', boot);
  document.addEventListener('shopify:block:deselect', boot);
  document.addEventListener('DOMContentLoaded', boot, { once: true });

  window[runtimeKey] = { boot, instances };
  boot();
})();
